/**
 * DOM resilience patch for browser page-translation (facebook/react#11538).
 *
 * Why: Edge/Chrome's built-in translator walks the page, wraps React-owned text
 * nodes in <font> elements and inserts translated text nodes. React's fiber tree
 * still references the original host nodes, so the next commit that deletes or
 * inserts children in a translated subtree throws DOMException NotFoundError from
 * parent.removeChild(child) / parent.insertBefore(node, ref) — hard-crashing to
 * the ErrorBoundary. Scoped translate="no" guards proved insufficient (the
 * translator corrupts subtrees we didn't guard), and a global translate="no" is
 * off the table: our Spanish-speaking users actively rely on translation.
 *
 * Behavior:
 * - Only the exact would-throw case is special-cased (child/reference not a
 *   child of the parent): removeChild becomes a no-op returning the child;
 *   insertBefore falls back to appending (reference = null).
 * - Every other call — including other DOMExceptions like
 *   HierarchyRequestError — delegates byte-for-byte to the originals.
 * - Each recovery is reported via reportWarning (component 'domGuard'), lazily
 *   imported and throttled so a translated session can neither break DOM ops
 *   nor flood the admin panel. A spike of domGuard warnings NOT correlated with
 *   translated sessions would flag a genuine reconciliation bug being masked.
 */

const INSTALL_FLAG = '__domResilienceInstalled';

type GuardedMethod = 'removeChild' | 'insertBefore';

// Report at most once per method per minute — a single translated commit can
// produce many mismatched ops, and one row per session is plenty of signal.
const REPORT_THROTTLE_MS = 60_000;
const lastReportAt: Record<GuardedMethod, number> = { removeChild: 0, insertBefore: 0 };

const reportRecovery = (method: GuardedMethod): void => {
    try {
        const now = Date.now();
        if (now - lastReportAt[method] < REPORT_THROTTLE_MS) return;
        lastReportAt[method] = now;
        // Lazy dynamic import: the logger pulls in supabase/breadcrumbs — a
        // failure there must never break DOM operations, and the async hop keeps
        // all reporting work out of React's synchronous commit phase.
        import('./logger')
            .then(({ reportWarning }) => {
                reportWarning(
                    `[domGuard] ${method}: node not a child of parent — browser page-translate suspected`,
                    { component: 'domGuard' }
                );
            })
            .catch(() => { /* logger unavailable — the DOM recovery already happened */ });
    } catch { /* never throw from the guard */ }
};

export const installDomResilience = (): void => {
    if (typeof window === 'undefined' || typeof Node === 'undefined') return;
    // Window flag (not just a module flag) so HMR re-evaluation or a double
    // import can't nest the wrappers.
    if ((window as any)[INSTALL_FLAG]) return;
    (window as any)[INSTALL_FLAG] = true;

    const originalRemoveChild = Node.prototype.removeChild;
    const originalInsertBefore = Node.prototype.insertBefore;

    Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
        if (child.parentNode !== this) {
            // Would throw NotFoundError — the translator already detached/moved it.
            reportRecovery('removeChild');
            return child;
        }
        return originalRemoveChild.call(this, child) as T;
    };

    Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
        if (referenceNode && referenceNode.parentNode !== this) {
            // Would throw NotFoundError — append instead. Ordering inside an
            // already-corrupted subtree may be briefly off; cosmetic only.
            reportRecovery('insertBefore');
            return originalInsertBefore.call(this, newNode, null) as T;
        }
        return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };
};
